import { View, type ViewStyle } from "@-/com.npmjs.react-native";
import { createAnimatedComponent, useAnimatedStyle } from "@-/com.npmjs.react-native-reanimated";
import type { HorizontalScrollEdgeFadeMaskProps } from "#horizontal-scroll-edge-fade-mask-props";
import { getHorizontalScrollEdgeFadeWidths } from "#horizontal-scroll-edge-fade-widths";
import { SCROLL_EDGE_FADE_HEIGHT } from "#scroll-edge-fade-layout";
import { SCROLL_EDGE_FADE_RAMP } from "#scroll-edge-fade-ramp";

const AnimatedView = createAnimatedComponent(View);

/** React Native's style types have no CSS mask; the web renderer passes it through. */
type WebMaskStyle = ViewStyle & { maskImage: string };

/**
 * Wraps a horizontal scroller and dissolves its content at whichever side still has more to
 * scroll to, with the same eased ramp as `ScrollEdgeFade`. It is a mask, not an overlay: the
 * content itself turns transparent, so it reads the same over an opaque bar, the space sky, or a
 * page passing beneath a floating glass toolbar, and no surface colour has to match.
 *
 * Web draws it as a CSS `mask-image` gradient whose sides widen as content hides past each edge;
 * with nothing to fade it sets no mask at all.
 */
export function HorizontalScrollEdgeFadeMask({
  children,
  className,
  edges,
  style,
  testID,
  width = SCROLL_EDGE_FADE_HEIGHT,
}: HorizontalScrollEdgeFadeMaskProps) {
  const { contentWidth, scrollX, viewportWidth } = edges;
  const maskStyle = useAnimatedStyle((): WebMaskStyle => {
    const { left, right } = getHorizontalScrollEdgeFadeWidths({
      contentWidth: contentWidth.get(),
      scrollX: scrollX.get(),
      viewportWidth: viewportWidth.get(),
      width,
    });
    if (left === 0 && right === 0) {
      return { maskImage: "none" };
    }
    // The ramp read from the edge inward, as `[fractionOfFadeFromEdge, alpha]`.
    const sideStops = SCROLL_EDGE_FADE_RAMP.map(
      ([fraction, alpha]) => [1 - fraction, alpha] as const,
    ).toReversed();
    const leftStops = sideStops.map(
      ([fromEdge, alpha]) => `rgba(0, 0, 0, ${1 - alpha}) ${fromEdge * left}px`,
    );
    const rightStops = sideStops
      .toReversed()
      .map(([fromEdge, alpha]) => `rgba(0, 0, 0, ${1 - alpha}) calc(100% - ${fromEdge * right}px)`);
    return { maskImage: `linear-gradient(to right, ${[...leftStops, ...rightStops].join(", ")})` };
  });
  return (
    <AnimatedView className={className} style={[style, maskStyle]} testID={testID}>
      {children}
    </AnimatedView>
  );
}
